import { type ProblemDetails, problemDetailsSchema } from '@tasks/contracts/common';

export class ApiError extends Error {
  constructor(readonly problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }

  get isExpiredToken(): boolean {
    return this.problem.type.endsWith('/token-expired');
  }

  /**
   * Estado servidor que acompaña al 409 de versión. La UI escribe esto en la caché en vez de
   * restaurar el snapshot previo, que borraría el cambio de otro usuario (diseño §11).
   */
  conflictCurrent<T>(): T | null {
    if (this.problem.status !== 409) return null;
    const current: unknown = this.problem['current'];
    return current === undefined ? null : (current as T);
  }
}

export type ApiClientOptions = {
  baseUrl?: string;
  getAccessToken?: () => string | null;
  /** Devuelve true si consiguió un access nuevo; solo entonces se reintenta (diseño §4). */
  onExpired?: () => Promise<boolean>;
  /** Un 401 que no es "caducado" significa sesión muerta: el cliente debe dejar de creerse dentro. */
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
};

/** What a person reads when the server refuses a write because their session is read-only. */
export const READ_ONLY_DETAIL =
  'You have read-only access to this Space, so this change was not made. Ask a Space admin for edit access.';

const problemFrom = async (response: Response): Promise<ProblemDetails> => {
  const fallback: ProblemDetails = {
    type: 'https://tasks/errors/internal',
    title: response.statusText,
    status: response.status,
  };
  const parsed = problemDetailsSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) return fallback;
  // a viewer's write is refused with 403 `read_only` (PolicyGuard): said in words, never as a raw «forbidden»
  return parsed.data.status === 403 && parsed.data['code'] === 'read_only'
    ? { ...parsed.data, detail: READ_ONLY_DETAIL }
    : parsed.data;
};

const buildHeaders = (init: RequestInit, token: string | null): Headers => {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  if (init.body !== undefined) headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);
  return headers;
};

const jsonBody = (value: unknown): RequestInit =>
  value === undefined ? {} : { body: JSON.stringify(value) };

/**
 * Avisa de la sesión muerta antes de propagar el error. Solo cuenta si la petición llevaba
 * token: un 401 sin credenciales pasa durante el arranque y no dice nada de la sesión guardada.
 */
const reject = (options: ApiClientOptions, error: ApiError, hadToken: boolean): ApiError => {
  if (hadToken && error.problem.status === 401) options.onUnauthorized?.();
  return error;
};

/** Envía la petición con el access vigente y las cabeceras JSON. */
const sender = (options: ApiClientOptions) => {
  const baseUrl = options.baseUrl ?? '/api/v1';
  return (path: string, init: RequestInit): Promise<Response> => {
    const headers = buildHeaders(init, options.getAccessToken?.() ?? null);
    // Resuelto en cada llamada: permite sustituir fetch tras crear el cliente (tests).
    const fetchImpl = options.fetchImpl ?? globalThis.fetch;
    return fetchImpl(`${baseUrl}${path}`, { ...init, headers });
  };
};

/**
 * Respuesta sin cuerpo. No basta con mirar el 204: `complete` de adjuntos responde 202 sin
 * contenido y `response.json()` fallaba con «Unexpected end of input».
 */
const isEmpty = (response: Response): boolean => {
  if (response.status === 204 || response.status === 205) return true;
  return response.headers.get('content-length') === '0';
};

const requester = (options: ApiClientOptions) => {
  const send = sender(options);

  /** Un único reintento y solo si el access caducó: token-reuse o inválido van a login. */
  const retryOrThrow = async (
    path: string,
    init: RequestInit,
    failed: { response: Response; hadToken: boolean },
  ): Promise<Response> => {
    const error = new ApiError(await problemFrom(failed.response));
    const had = failed.hadToken;
    if (!error.isExpiredToken || !(await options.onExpired?.())) throw reject(options, error, had);
    const retried = await send(path, init);
    if (!retried.ok) throw reject(options, new ApiError(await problemFrom(retried)), had);
    return retried;
  };

  return async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const hadToken = (options.getAccessToken?.() ?? null) !== null;
    const first = await send(path, init);
    const response = first.ok
      ? first
      : await retryOrThrow(path, init, { response: first, hadToken });
    return isEmpty(response) ? (undefined as T) : ((await response.json()) as T);
  };
};

export const createApiClient = (options: ApiClientOptions = {}) => {
  const request = requester(options);
  return {
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, payload?: unknown) =>
      request<T>(path, { method: 'POST', ...jsonBody(payload) }),
    patch: <T>(path: string, payload: unknown) =>
      request<T>(path, { method: 'PATCH', ...jsonBody(payload) }),
    put: <T>(path: string, payload: unknown) =>
      request<T>(path, { method: 'PUT', ...jsonBody(payload) }),
    delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  };
};

export type ApiClient = ReturnType<typeof createApiClient>;
